import { motion } from "motion/react";
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  CursorArrowRaysIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";
import { SearchBar } from "../SearchBar";
import MigrationBanner from "./MigrationBanner";

const ACCENT = "#4d7c0f";

// One icon per outcome — the pills read as what Fluvo moves, not as channels.
const OUTCOMES = [
  { label: "Conversions", icon: ArrowTrendingUpIcon },
  { label: "Engagement", icon: CursorArrowRaysIcon },
  { label: "Retention", icon: HeartIcon },
  { label: "Win-backs", icon: ArrowPathIcon },
];

const rise = (delay = 0) => ({
  initial: { y: 40, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { ease: "easeOut", duration: 0.5, delay },
});

export default function HeroWithCenteredImage() {
  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden px-6 pt-36 pb-24 lg:px-10 lg:pt-44"
      style={{ background: "#fafafa", fontFamily: "var(--font-geist)" }}
    >
      {/* Soft accent wash falling from the top edge */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[600px]"
        style={{
          background: `radial-gradient(ellipse 70% 60% at 50% 0%, ${ACCENT}14, transparent 70%)`,
        }}
      />

      <div className="relative mx-auto w-full max-w-6xl text-center text-neutral-900">
        <motion.div
          {...rise()}
          className="mb-8 inline-flex h-7 items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 text-[12px] text-neutral-500"
          style={{ fontFamily: "var(--font-geist-mono)" }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: ACCENT }}
          />
          AI Powered Straight From Your Database
        </motion.div>

        <motion.h1
          {...rise()}
          className="text-[clamp(40px,7.5vw,112px)] font-medium leading-[0.95] tracking-[-0.035em]"
        >
          Customer journeys that
          <br className="hidden md:inline" /> run{" "}
          <span
            className="italic"
            style={{
              color: ACCENT,
              fontFamily: "var(--font-newsreader)",
              fontWeight: 400,
            }}
          >
            themselves
          </span>
        </motion.h1>

        <motion.p
          {...rise(0.2)}
          className="mx-auto mt-8 max-w-2xl text-[18px] leading-[1.5] text-neutral-500"
        >
          Fluvo knows who to reach, where they are in their journey, and what
          should happen next.
        </motion.p>

        <motion.div
          {...rise(0.3)}
          className="mt-6 flex flex-wrap items-center justify-center gap-2"
        >
          {OUTCOMES.map(({ label, icon: Icon }) => (
            <span
              key={label}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 text-[12.5px] text-neutral-600"
            >
              <Icon className="size-3.5" style={{ color: ACCENT }} />
              {label}
            </span>
          ))}
        </motion.div>

        <motion.div {...rise(0.4)} className="mt-10 flex justify-center">
          <SearchBar variant="pill" />
        </motion.div>

        <motion.div
          {...rise(0.5)}
          className="mt-3 text-[12.5px] text-neutral-400"
          style={{ fontFamily: "var(--font-geist-mono)" }}
        >
          Fluvo only required your website URL
        </motion.div>

        <motion.div {...rise(0.6)}>
          <MigrationBanner className="px-0 pt-14 md:pt-16 lg:px-0" />
        </motion.div>
      </div>
    </section>
  );
}
